import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, FileCheck2, Eye, Activity, Play, Languages } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';
import PageHeader from '../components/PageHeader';

const STATUS_STYLE = {
  consenting: 'bg-stone-100 text-stone-700',
  generating: 'bg-blue-100 text-blue-800',
  awaiting_review: 'bg-amber-100 text-amber-800',
  released: 'bg-emerald-100 text-emerald-800',
  revoked: 'bg-rose-100 text-rose-700',
};

const GENERATE_ENDPOINTS = {
  CR: '/api/admin/reports/court/generate',
  CM: '/api/admin/reports/case-management/generate',
  SP: '/api/admin/reports/sponsor/generate',
  EM: '/api/admin/reports/employer/generate',
  IN: '/api/admin/reports/insurance/generate',
};

export default function SuperAdminReportsPage() {
  const [reports, setReports] = useState([]);
  const [reportsReviewedFlag, setReportsReviewedFlag] = useState(false);
  const [loading, setLoading] = useState(true);
  const [audit, setAudit] = useState(null);
  const [generating, setGenerating] = useState(null);
  const [translating, setTranslating] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/reports/all`);
    if (r.ok) {
      const d = await r.json();
      setReports(d.reports || []);
      setReportsReviewedFlag(!!d.reports_reviewed);
    } else toast.error('Failed to load reports');
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const openAudit = async (consent_id) => {
    const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/reports/audit/${consent_id}`);
    if (r.ok) setAudit({ consent_id, events: (await r.json()).events });
  };

  const generateReport = async (consent_id, report_type) => {
    const ep = GENERATE_ENDPOINTS[report_type];
    if (!ep) { toast.error(`No generator for ${report_type}`); return; }
    setGenerating(consent_id);
    try {
      const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}${ep}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ consent_id }),
      });
      if (r.ok) { toast.success('Draft generated. Awaiting user review.'); refresh(); }
      else { const e = await r.json().catch(()=>({})); toast.error(e.detail || 'Generation failed'); }
    } finally { setGenerating(null); }
  };

  const translateReport = async (consent_id) => {
    const lang = window.prompt('Target language code (ru, uk, ko, fr, it, es, pt):');
    if (!lang) return;
    setTranslating(consent_id);
    try {
      const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/reports/${consent_id}/translate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_language: lang.toLowerCase().trim() }),
      });
      if (r.ok) toast.success(`Translated to ${lang}`);
      else { const e = await r.json().catch(()=>({})); toast.error(e.detail || 'Translation failed'); }
    } finally { setTranslating(null); }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="superadmin-reports-page">
      <PageHeader title="Clinical &amp; Administrative Reports" backTo="/superadmin" icon={FileCheck2} />
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-4">
        {!reportsReviewedFlag && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900" data-testid="reports-pending-review">
            <b>Awaiting clinical review.</b>  The AI prompts have not yet been signed off by a qualified clinician.
            Only SuperAdmins can currently generate reports: regular users see a 503 until <code className="font-mono">REPORTS_REVIEWED=true</code> in <code>backend/.env</code>.
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-5">
          <h2 className="text-lg font-semibold text-stone-900">All reports</h2>
          <p className="text-sm text-stone-600 mt-1">Court (CR), Case Management (CM), Sponsor (SP), Employer (EM), Insurance (IN).  Every transition is in the append-only audit log.</p>

          {loading ? (
            <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin inline text-stone-500" /></div>
          ) : reports.length === 0 ? (
            <div className="text-center py-12 text-stone-500" data-testid="no-reports-empty">No reports generated yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="reports-table">
                <thead className="text-stone-500 uppercase text-xs">
                  <tr className="border-b border-stone-200">
                    <th className="text-left py-2 pr-3">Ref</th>
                    <th className="text-left py-2 pr-3">Type</th>
                    <th className="text-left py-2 pr-3">Subject</th>
                    <th className="text-left py-2 pr-3">Recipient</th>
                    <th className="text-left py-2 pr-3">Status</th>
                    <th className="text-left py-2 pr-3">Created</th>
                    <th className="text-right py-2">Audit</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map(r => (
                    <tr key={r.consent_id} className="border-b border-stone-100" data-testid={`report-row-${r.consent_id}`}>
                      <td className="py-2.5 pr-3 font-mono text-xs">{r.report_ref}</td>
                      <td className="py-2.5 pr-3">
                        <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold bg-stone-100 text-stone-700">{r.report_type}</span>
                      </td>
                      <td className="py-2.5 pr-3 text-stone-900">{r.user_name_snapshot}</td>
                      <td className="py-2.5 pr-3 text-stone-700 text-xs">
                        {r.recipient?.name}{r.recipient?.organisation ? ` · ${r.recipient.organisation}` : ''}
                      </td>
                      <td className="py-2.5 pr-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_STYLE[r.status] || 'bg-stone-100 text-stone-700'}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="py-2.5 pr-3 text-xs text-stone-500">{r.created_at?.slice(0, 10)}</td>
                      <td className="py-2.5 text-right">
                        <div className="inline-flex gap-1">
                          {r.status === 'consenting' && (
                            <button onClick={() => generateReport(r.consent_id, r.report_type)}
                              disabled={generating === r.consent_id}
                              className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full text-xs disabled:opacity-50"
                              data-testid={`generate-btn-${r.consent_id}`}>
                              {generating === r.consent_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />} Generate
                            </button>
                          )}
                          {(r.status === 'awaiting_review' || r.status === 'released') && (
                            <button onClick={() => translateReport(r.consent_id)}
                              disabled={translating === r.consent_id}
                              className="inline-flex items-center gap-1 px-2.5 py-1 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-full text-xs disabled:opacity-50"
                              data-testid={`translate-btn-${r.consent_id}`}>
                              {translating === r.consent_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Languages className="w-3 h-3" />} Translate
                            </button>
                          )}
                          <button onClick={() => openAudit(r.consent_id)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-full text-xs"
                            data-testid={`audit-btn-${r.consent_id}`}>
                            <Eye className="w-3 h-3" /> Audit
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {audit && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={() => setAudit(null)} data-testid="audit-modal">
          <div className="bg-white rounded-2xl p-5 max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-stone-900 inline-flex items-center gap-2"><Activity className="w-4 h-4" /> Audit trail</h3>
              <button onClick={() => setAudit(null)} className="text-stone-400 hover:text-stone-700 text-lg">×</button>
            </div>
            <p className="text-xs text-stone-500 mb-3">Append-only.  {audit.consent_id}</p>
            <ul className="space-y-2">
              {audit.events.map((e, i) => (
                <li key={i} className="border-l-2 border-stone-300 pl-3 py-1 text-xs">
                  <div className="font-mono text-stone-900">{e.event}</div>
                  <div className="text-stone-500">{e.at?.slice(0, 19).replace('T', ' ')} UTC · by {e.actor}</div>
                  {e.detail && Object.keys(e.detail).length > 0 && (
                    <pre className="mt-1 bg-stone-50 rounded p-1.5 text-[10px] overflow-x-auto">{JSON.stringify(e.detail, null, 2)}</pre>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
